import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../app/LanguageContext';

function SortableTreeRow({
  node,
  depth,
  expanded,
  onToggle,
  onDelete,
  disabled,
}) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const hasChildren = (node.children || []).length > 0;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disablePadding
      secondaryAction={
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => navigate(`/products/categories/${encodeURIComponent(node.id)}/edit`)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete?.(node)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      <ListItemButton sx={{ pl: 2 + depth * 2, pr: 10 }} onClick={() => navigate(`/products/categories/${encodeURIComponent(node.id)}/edit`)}>
        <IconButton
          size="small"
          disabled={disabled}
          {...attributes}
          {...listeners}
          sx={{ mr: 0.5, cursor: disabled ? 'default' : 'grab' }}
          onClick={(e) => e.stopPropagation()}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.(node.id);
            }}
          >
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 32 }} />
        )}
        <ListItemText
          primary={node.name}
          secondary={node.slug}
          primaryTypographyProps={{ fontWeight: depth === 0 ? 600 : 400 }}
        />
        <Chip
          label={node.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
          size="small"
          color={node.status === 'ACTIVE' ? 'success' : 'default'}
          sx={{ ml: 1, height: 22 }}
        />
      </ListItemButton>
    </ListItem>
  );
}

function TreeBranch({ nodes, depth, expandedIds, onToggle, onDelete, disabled, parentId }) {
  const ids = (nodes || []).map((n) => n.id);
  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      {(nodes || []).map((node) => (
        <Box key={node.id}>
          <SortableTreeRow
            node={node}
            depth={depth}
            expanded={expandedIds.has(node.id)}
            onToggle={onToggle}
            onDelete={onDelete}
            disabled={disabled}
          />
          <Collapse in={expandedIds.has(node.id)} timeout="auto" unmountOnExit>
            <TreeBranch
              nodes={node.children}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onDelete={onDelete}
              disabled={disabled}
              parentId={node.id}
            />
          </Collapse>
        </Box>
      ))}
    </SortableContext>
  );
}

export default function CategoryTree({ tree = [], onReorder, onDelete, disabled = false }) {
  const { translate } = useTranslation();
  const [expandedIds, setExpandedIds] = useState(() => new Set(tree.map((n) => n.id)));
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const rootIds = useMemo(() => (tree || []).map((n) => n.id), [tree]);
  const activeNode = useMemo(() => {
    const find = (nodes) => {
      for (const n of nodes || []) {
        if (n.id === activeId) return n;
        const child = find(n.children);
        if (child) return child;
      }
      return null;
    };
    return find(tree);
  }, [activeId, tree]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder?.({ activeId: active.id, overId: over.id, parentId: '' });
  };

  if (!tree.length) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {translate('categories.empty_tree')}
      </Typography>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={handleDragEnd}
    >
      <List disablePadding>
        <TreeBranch
          nodes={tree}
          depth={0}
          expandedIds={expandedIds}
          onToggle={toggleExpand}
          onDelete={onDelete}
          disabled={disabled}
        />
      </List>
      <DragOverlay>
        {activeNode ? (
          <Box sx={{ px: 2, py: 1, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 1 }}>
            {activeNode.name}
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
